/**
 * Responsibility: persist and restore a world across browser sessions.
 *
 * ## Why a save is tiny
 *
 * Terrain is a pure function of `(seed, x, z)`, so nothing about the world needs storing — only where
 * the player was in it. A save is a seed plus a position, comfortably under a kilobyte, and it cannot
 * become stale or inconsistent because there is no world state to be inconsistent with.
 *
 * ## What is deliberately not saved
 *
 * Roll and pitch. Resuming mid-bank would tilt the world before the player had touched anything, so a
 * loaded world always starts level. Everything else about the aircraft's attitude is a consequence of
 * input rather than a fact worth keeping.
 *
 * ## Degrading rather than failing
 *
 * Every way of loading can go wrong: no save, malformed JSON, a version from a future build, a field
 * someone hand-edited to a string. All of them return a plain "no usable save" rather than throwing,
 * because the correct response to every one of them is the same — start a fresh world. A player whose
 * save cannot be read wants to fly, not to see an error they cannot act on.
 *
 * `localStorage` itself can also throw, not just return null: Safari in private mode denies access,
 * and writes fail when the origin's quota is full. Both are caught.
 *
 * Contains no Three.js and no DOM access beyond the injected storage, so it is fully unit testable.
 */

import { SAVE } from '../config.js';

/** Reasons a load can come back empty. Exposed so callers and tests can be specific. */
export const LOAD_FAILURE = Object.freeze({
  ABSENT: 'absent',
  UNREADABLE: 'unreadable',
  CORRUPT: 'corrupt',
  VERSION: 'version',
  INVALID: 'invalid',
});

/** Fields that must be present and finite for a save to be usable. */
const REQUIRED_NUMBERS = ['worldX', 'worldZ', 'heading', 'altitude', 'speedTrim'];

/**
 * Build the plain object to persist.
 *
 * @param {object} options
 * @param {number|string} options.seed - The world seed, exactly as it was normalised for generation.
 * @param {{x: number, y: number, z: number, heading: number, speedTrim: number}} options.flightState
 * @param {string} options.aircraftId
 * @returns {object} Ready for `JSON.stringify`.
 */
export function serializeSave({ seed, flightState, aircraftId, mode = 'fly', train = null }) {
  return {
    version: SAVE.VERSION,
    seed,
    worldX: flightState.x,
    worldZ: flightState.z,
    altitude: flightState.y,
    heading: flightState.heading,
    speedTrim: flightState.speedTrim,
    aircraftId,

    /*
      **Both modes are always saved, whichever one is being played.** A rider who switches to flying and saves
      should not lose their place on the line, and a pilot who takes the train should come back to the same
      patch of sky. The flying fields above are therefore written even in train mode, from the last flight
      state the session had.

      Optional on read rather than required: an existing save predates all of this, and a flying save is a
      complete save. See `validateSave`.
    */
    mode,
    trainDistance: train === null ? 0 : train.distance,
    trainStep: train === null ? null : train.step,

    /*
      Not read back by anything — it exists so a save is legible if someone opens it in devtools, which
      is the only debugging tool available for a problem that only reproduces on someone else's machine.
    */
    savedAt: new Date().toISOString(),
  };
}

/**
 * Validate and normalise a parsed save.
 *
 * Separated from JSON parsing so the validation rules can be tested against objects directly.
 *
 * @param {unknown} parsed
 * @returns {{ok: true, save: object} | {ok: false, reason: string}}
 */
export function validateSave(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return { ok: false, reason: LOAD_FAILURE.CORRUPT };
  }

  /*
    Version checked before anything else. A save from a future build may have fields that look valid
    and mean something different, which is worse than one that is obviously broken.
  */
  if (parsed.version !== SAVE.VERSION) {
    return { ok: false, reason: LOAD_FAILURE.VERSION };
  }

  for (const field of REQUIRED_NUMBERS) {
    if (!Number.isFinite(parsed[field])) {
      return { ok: false, reason: LOAD_FAILURE.INVALID };
    }
  }

  /*
    The seed may legitimately be a number or a phrase, so it is only checked for being one of those.
    `normalizeSeed` in `world/rng.js` is the authority on what a seed means, and duplicating its rules
    here would be a second place to keep them.
  */
  if (typeof parsed.seed !== 'number' && typeof parsed.seed !== 'string') {
    return { ok: false, reason: LOAD_FAILURE.INVALID };
  }

  /*
    An unknown aircraft id is *not* a failure. The registry resolves it with a fallback, so a save
    naming a model that has since been renamed still puts the player back where they were.
  */
  return {
    ok: true,
    save: {
      seed: parsed.seed,
      worldX: parsed.worldX,
      worldZ: parsed.worldZ,
      altitude: parsed.altitude,
      heading: parsed.heading,
      speedTrim: parsed.speedTrim,
      aircraftId: typeof parsed.aircraftId === 'string' ? parsed.aircraftId : null,

      /*
        **Optional, and absent means flying.** Every save written before the train existed is a complete,
        valid save of a flight, so putting these in `REQUIRED_NUMBERS` would have discarded all of them for
        nothing. Anything unrecognised falls back the same way, which is the per-field degradation the
        settings store already uses — one unreadable field should not cost the player their world.
      */
      mode: parsed.mode === 'train' ? 'train' : 'fly',
      trainDistance: Number.isFinite(parsed.trainDistance) ? parsed.trainDistance : 0,
      trainStep: Number.isFinite(parsed.trainStep) ? parsed.trainStep : null,
    },
  };
}

/**
 * Create the save store.
 *
 * @param {object} [options]
 * @param {Storage} [options.storage] - Defaults to `localStorage`. Injected so the logic can be
 *   tested without a browser, and so a storage-less environment degrades instead of throwing.
 * @param {string} [options.key]
 * @returns {object}
 */
export function createSaveStore({ storage = defaultStorage(), key = SAVE.KEY } = {}) {
  return {
    /**
     * Write a save.
     *
     * @param {object} payload - Same shape `serializeSave` takes.
     * @returns {{ok: boolean, reason?: string, bytes?: number}} Failure is reported rather than
     *   thrown: a save that could not be written is worth telling the player about, but it must not
     *   interrupt their flight.
     */
    write(payload) {
      if (!storage) return { ok: false, reason: LOAD_FAILURE.UNREADABLE };

      try {
        const text = JSON.stringify(serializeSave(payload));
        storage.setItem(key, text);
        return { ok: true, bytes: text.length };
      } catch (error) {
        return { ok: false, reason: LOAD_FAILURE.UNREADABLE, error };
      }
    },

    /**
     * Read the save, if there is a usable one.
     *
     * @returns {{ok: true, save: object} | {ok: false, reason: string}}
     */
    read() {
      if (!storage) return { ok: false, reason: LOAD_FAILURE.UNREADABLE };

      let text;
      try {
        text = storage.getItem(key);
      } catch (error) {
        // Private-mode Safari throws on access rather than returning null.
        return { ok: false, reason: LOAD_FAILURE.UNREADABLE, error };
      }

      if (text === null || text === undefined || text === '') {
        return { ok: false, reason: LOAD_FAILURE.ABSENT };
      }

      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        return { ok: false, reason: LOAD_FAILURE.CORRUPT };
      }

      return validateSave(parsed);
    },

    /** Whether a usable save exists, without loading it. For enabling a menu button. */
    exists() {
      return this.read().ok;
    },

    /** Remove the save. Reported rather than thrown, for the same reason as `write`. */
    clear() {
      if (!storage) return { ok: false, reason: LOAD_FAILURE.UNREADABLE };

      try {
        storage.removeItem(key);
        return { ok: true };
      } catch (error) {
        return { ok: false, reason: LOAD_FAILURE.UNREADABLE, error };
      }
    },
  };
}

/**
 * `localStorage`, or null where it is unavailable.
 *
 * Merely *touching* `localStorage` throws in some configurations, so even acquiring it is guarded.
 * Returning null lets the store degrade to "saving is unavailable" rather than taking down startup.
 */
function defaultStorage() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
